import {
  CustomPagination,
  DataObjectGroupProcessingStateFilterKey,
  deserializeGroupDescription,
  deserializeValueDate,
  EMPTY_PAGE,
  FormattedDate,
  formatDate,
  getIsoDateFromFormattedDate,
  InputProcessingEventsFilterKey,
  ISODate,
  Main,
  OutputDescriptionProcessingStatesParams,
  Padding,
  serializeGroupDescription,
  serializeValueDate,
  Spinner,
  Stack,
  TotalEntries,
  useDirectory,
  useOutputDataGroupProcessingStateQuery,
  useQueryParams,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import { Alert, Flex, FormInstance } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { OutputDataHeader } from './OutputDataHeader';
import {
  MIXED_OUTPUT_TYPE_FILTER_VALUE,
  OutputDataDirectoryTable,
} from './OutputDataDirectoryTable';
import { useRefreshOutputDataGroupProcessingStates } from './useRefreshOutputDataGroupProcessingStates';
import { OutputDataDirectoryFilter } from './OutputDataDirectoryFilter';
import {
  OutputDataContextProvider,
  useOutputDataStore,
} from './OutputDataContext';
import { renderOutputDataActionModal } from './actionModals/renderOutputDataActionModal';
import { RelationsTablesModalParams } from '../relations/RelationsTablesModal';
import { useDaysToSetQueryParamsPeriod } from '../input-data/utils';

export const OutputDataActionType = {
  ShowRelated: 'showRelated',
  ShowConfirmations: 'showConfirmations',
  Calculate: 'calculate',
  Confirm: 'confirm',
  ConfirmAndCalculate: 'confirmAndCalculate',
  ChangeConfirmStatus: 'changeConfirmStatus',
  Delete: 'delete',
  Validate: 'validate',
  CalculateAndValidate: 'calculateAndValidate',
  CancelCalculationErrors: 'cancelCalculationErrors',
} as const;

export type OutputDataActionType =
  (typeof OutputDataActionType)[keyof typeof OutputDataActionType];

export const OUTPUT_DATA_TIME_SERIES_ACTIONS = [
  //'timeSeriesLockInputs',
  'timeSeriesCalculate',
  'timeSeriesValidate',
  'timeSeriesCalculateAndValidate',
  'timeSeriesConfirm',
  'timeSeriesConfirmAndCalculate',
  //'timeSeriesUnlockInputs',
  'timeSeriesChangeConfirmStatus',
  //'timeSeriesSeasonalSmoothing',
] as const;

export type OutputDataTimeSeriesActionKey =
  (typeof OUTPUT_DATA_TIME_SERIES_ACTIONS)[number];

export type ModalActionKey =
  | OutputDataActionType
  | OutputDataTimeSeriesActionKey;

export type OutputDataActionParams = OutputDescriptionProcessingStatesParams & {
  hasAccess: boolean;
};

export type OutputDataAction =
  | {
      type: OutputDataActionType;
      params: OutputDataActionParams;
    }
  | { type: 'openRelationsTable'; params: RelationsTablesModalParams };

export type TimeSeriesAction = {
  type: OutputDataTimeSeriesActionKey;
};

export type OutputDataModalAction = OutputDataAction | TimeSeriesAction;

const FILTER_KEYS: DataObjectGroupProcessingStateFilterKey[] = [
  'name',
  'userFollowedDataObjectGroups',
  'valueDays',
  'valueDate',
  'onlyLastValueDate',
  'domainId',
  'frequencyId',
  'username',
  'dueDateTime',
  'outputTypeId',
  'outputCode',
  'sourceCode',
  'processingStateCode',
];

const arrayFields: DataObjectGroupProcessingStateFilterKey[] = [
  'domainId',
  'frequencyId',
  'valueDate',
  'dueDateTime',
  'outputTypeId',
  'processingStateCode',
];

const numericFields: DataObjectGroupProcessingStateFilterKey[] = [
  'valueDays',
  'domainId',
  'frequencyId',
];

const booleanFields: InputProcessingEventsFilterKey[] = [
  'userFollowedDataObjectGroups',
  'onlyLastValueDate',
];

const serializeProcessingStateFilter = (value: unknown) =>
  Array.isArray(value) && value.includes('IN_PROGRESS')
    ? Array.from(new Set([...value, 'PENDING']))
    : value;

const deserializeProcessingStateFilter = (value: unknown) =>
  Array.isArray(value)
    ? Array.from(
        new Set(
          value.map((processingState) =>
            processingState === 'PENDING' ? 'IN_PROGRESS' : processingState,
          ),
        ),
      )
    : value;

const deserializeOutputTypeFilter = (value?: (number | string | null)[]) =>
  value?.map((outputTypeId) =>
    outputTypeId === null || outputTypeId === MIXED_OUTPUT_TYPE_FILTER_VALUE
      ? MIXED_OUTPUT_TYPE_FILTER_VALUE
      : Number(outputTypeId),
  );

export const getApiOutputTypeFilter = (value?: (number | string)[]) => {
  const outputTypeId = value
    ?.filter((item) => item !== MIXED_OUTPUT_TYPE_FILTER_VALUE)
    .map(Number);

  return {
    outputTypeId: outputTypeId?.length ? outputTypeId : undefined,
    includeMixedOutputType: value?.includes(MIXED_OUTPUT_TYPE_FILTER_VALUE)
      ? true
      : undefined,
  };
};

export function OutputDataDirectory() {
  useDaysToSetQueryParamsPeriod();

  const [{ valueDays }] = useQueryParams();
  if (valueDays) {
    return <Spinner />;
  }

  return (
    <OutputDataContextProvider>
      <OutputDataDirectoryContent />
    </OutputDataContextProvider>
  );
}

export function OutputDataDirectoryContent() {
  const filtersRef = useRef<FormInstance>(null);

  const {
    headerProps,
    tableProps,
    paginationProps,
    sidePanelFilterProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    arrayFields,
    numericFields,
    booleanFields,
    initialFilters: { onlyLastValueDate: true },
    initialSort: [{ id: 'valueDate', desc: true }],
    sidePanelFilterRef: filtersRef,
    serializeFilter: (...args) => {
      const [[id, value], ...rest] = Object.entries(
        serializeValueDate(args[0], args[1]),
      );
      if (id === 'name') {
        return serializeGroupDescription(id, value);
      }
      return {
        [id]:
          id === 'dueDateTime'
            ? value?.map(
                (d: FormattedDate) =>
                  getIsoDateFromFormattedDate(d) + 'T00:00:00',
              )
            : id === 'processingStateCode'
              ? serializeProcessingStateFilter(value)
              : value,
        ...Object.fromEntries(rest),
      };
    },
    deserializeFilter: (id, value, allQueryFilters) => {
      if (id === 'dueDateTime') {
        return { [id]: value?.map((d: ISODate) => formatDate(d)) };
      } else if (id === 'name' || id === 'userFollowedDataObjectGroups') {
        return deserializeGroupDescription(id, value, allQueryFilters);
      } else if (id === 'processingStateCode') {
        return {
          [id]: deserializeProcessingStateFilter(value),
        };
      } else if (id === 'outputTypeId') {
        return {
          [id]: deserializeOutputTypeFilter(value),
        };
      } else {
        return deserializeValueDate(id, value, allQueryFilters);
      }
    },
  });

  const [loadingError, setLoadingError] = useOutputDataStore('loadingError');
  const [activeAction, setActiveAction] =
    useState<OutputDataModalAction | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isActionDropdownOpenRef = useRef(false);
  const isActionModalOpen = activeAction !== null;
  const isAutoRefreshPaused = useCallback(
    () => isActionModalOpen || isActionDropdownOpenRef.current,
    [isActionModalOpen],
  );

  const {
    data: { content, totalElements } = EMPTY_PAGE,
    isFetching,
    refetch,
  } = useOutputDataGroupProcessingStateQuery(
    {
      ...queryParameters,
      ...getApiOutputTypeFilter(queryParameters.outputTypeId),
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  const { data: updatedContent, refresh } =
    useRefreshOutputDataGroupProcessingStates(content, {
      isAutoRefreshPaused,
    });

  const handleCloseModal = useCallback(() => {
    setActiveAction(null);
    refresh();
  }, [refresh]);

  const handleAction = useCallback(
    (action: OutputDataModalAction) => setActiveAction(action),
    [],
  );
  const handleActionDropdownOpenChange = useCallback((open: boolean) => {
    isActionDropdownOpenRef.current = open;
  }, []);

  return (
    <Main>
      <Main.Col border={false}>
        {loadingError && (
          <Alert
            banner
            type="error"
            message={loadingError}
            onClose={() => setLoadingError(undefined)}
            closable
          />
        )}
        <Main.Content padding={false} id="output-data-directory-content">
          {activeAction &&
            renderOutputDataActionModal(activeAction, handleCloseModal)}
          <DirectoryWrapper size="md">
            <Padding top horizontal>
              <OutputDataHeader
                {...headerProps}
                infoIconUrl={InfoIconUrl.OUTPUT_DATA_DIRECTORY}
                valueDate={queryParameters.valueDate}
                onFilterToggle={() => setIsFilterOpen((prev) => !prev)}
                isFilterOpen={isFilterOpen}
                onRefresh={refetch}
                isLoading={isFetching}
                onTimeSeriesAction={(actionKey) =>
                  setActiveAction({ type: actionKey })
                }
              />
            </Padding>
            <Padding bottom horizontal>
              <Flex vertical gap="small">
                <OutputDataDirectoryTable
                  data={updatedContent || []}
                  isLoading={isFetching}
                  onAction={handleAction}
                  onActionDropdownOpenChange={handleActionDropdownOpenChange}
                  {...tableProps}
                />
              </Flex>
            </Padding>
          </DirectoryWrapper>
        </Main.Content>
        <Main.Footer>
          <TotalEntries count={totalElements ?? 0} />
          <FooterPagination {...paginationProps} />
        </Main.Footer>
      </Main.Col>
      {isFilterOpen && (
        <OutputDataDirectoryFilter
          ref={filtersRef}
          onClose={() => setIsFilterOpen(false)}
          initialValues={queryParameters}
          {...sidePanelFilterProps}
        />
      )}
    </Main>
  );
}

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const DirectoryWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
